import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';

/**
 * Unified quest label component for consistent badge styling across the application
 * @param {Object} props - Component props
 * @param {string} props.type - Label type: 'xp', 'difficulty', 'sideQuests', 'premium', 'time', 'status', 'custom'
 * @param {string|number} props.value - The value to display (XP amount, difficulty level, status, count, etc.)
 * @param {string} [props.text] - Custom text (overrides auto-generated text)
 * @param {Object} [props.icon] - FontAwesome icon to display
 * @param {string} [props.color] - Color variant: 'primary', 'success', 'warning', 'error', 'neutral', or custom CSS color
 * @param {string} [props.size='default'] - Size variant: 'small', 'default', 'large'
 * @param {boolean} [props.showIcon=true] - Whether to show the icon
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {JSX.Element|null} Quest label component
 */
export default function QuestLabel({
  type,
  value,
  text,
  icon,
  color,
  size = 'default',
  showIcon = true,
  className = ''
}) {
  // Get size classes
  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'quest-label-small';
      case 'large': return 'quest-label-large';
      default: return 'quest-label-default';
    }
  };

  // Get configuration based on type
  const getTypeConfig = () => {
    switch (type) {
      case 'xp':
        return {
          icon: ICONS.COINS,
          text: `${value} XP`,
          colorClass: 'quest-label-xp'
        };
      case 'difficulty':
        const difficultyColors = {
          easy: 'quest-label-success',
          medium: 'quest-label-warning',
          hard: 'quest-label-error'
        };
        return {
          icon: null,
          text: value?.toUpperCase(),
          colorClass: difficultyColors[value] || 'quest-label-neutral'
        };
      case 'sideQuests':
        return {
          icon: ICONS.LIST,
          text: `${value} Side Quest${value !== 1 ? 's' : ''}`,
          colorClass: 'quest-label-neutral'
        };
      case 'premium':
        return {
          icon: ICONS.CROWN || ICONS.STAR,
          text: text || 'Premium',
          colorClass: 'quest-label-premium'
        };
      case 'time':
        return {
          icon: ICONS.CLOCK,
          text: value,
          colorClass: 'quest-label-neutral'
        };
      case 'status':
        const statusConfig = {
          pending: { icon: ICONS.CLOCK, text: 'Pending', colorClass: 'quest-label-status-pending' },
          approved: { icon: ICONS.CHECK_CIRCLE, text: 'Approved', colorClass: 'quest-label-status-approved' },
          rejected: { icon: ICONS.TIMES_CIRCLE, text: 'Rejected', colorClass: 'quest-label-status-rejected' },
          completed: { icon: ICONS.CHECK_CIRCLE, text: 'Completed', colorClass: 'quest-label-status-completed' },
          failed: { icon: ICONS.TIMES_CIRCLE, text: 'Failed', colorClass: 'quest-label-status-failed' },
          active: { icon: ICONS.PLAY, text: 'Active', colorClass: 'quest-label-status-active' }
        };
        return statusConfig[value] || { icon: ICONS.INFO, text: value, colorClass: 'quest-label-neutral' };
      case 'custom':
      default:
        return {
          icon: icon,
          text: text || value,
          colorClass: color ? `quest-label-${color}` : 'quest-label-neutral'
        };
    }
  };

  const config = getTypeConfig();

  // Don't render if no value/text
  if (!config.text && !text) return null;

  // Override with props if provided
  const displayIcon = icon !== undefined ? icon : config.icon;
  const displayText = text || config.text;
  const colorClass = color ? `quest-label-${color}` : config.colorClass;

  return (
    <span className={`quest-label ${getSizeClass()} ${colorClass} ${className}`}>
      {showIcon && displayIcon && <FontAwesomeIcon icon={displayIcon} className="quest-label-icon" />}
      <span className="quest-label-text">{displayText}</span>
    </span>
  );
}
